import { clsx } from 'clsx';
import { useEffect, useState } from 'react';
import { Button, Checkbox, Input, Modal, notification, Radio, Space } from 'antd';
import { CheckOutlined, PlusOutlined, SaveFilled } from '@ant-design/icons';

import { ConfirmDelete } from '@components';
import { adminSettingsHttp } from '@network';
import { AdminOptionCats, AdminSettingsOption } from '@types';
import cls from './SettingsOptions.module.css';


interface Props {
  maxLength?: number;
  canMerge?: boolean;
  category: AdminOptionCats;
  readonly?: boolean;
  title: string;
}

interface NewItemProps {
  onSave: (title: string) => void;
  onClose: () => void;
}

const MergeIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 48 48"
    width="24"
    height="24"
    fill="var(--primary-color)"
    className={clsx(cls.icon, cls.mergeBtnIcon)}
  >
    <path d="M34 40.83 36.83 38 30 31.17 27.17 34 34 40.83zM15 16h7v11.17L11.17 38 14 40.83l12-12V16h7l-9-9-9 9z"/>
  </svg>
);

const LockIcon = ({ className }: { className?: string }) => (
  <svg
    width="10"
    height="14"
    viewBox="0 0 10 14"
    fill="none"
    className={className}
    xmlns="http://www.w3.org/2000/svg">
    <path d="M5 0C5.52518 0 6.04505 0.116716 6.53027 0.342773C7.01558 0.56892 7.45669 0.900496 7.82812 1.31836C8.19954 1.73621 8.4943 2.23237 8.69531 2.77832C8.86965 3.2519 8.97046 3.75487 8.99414 4.26562C9.59483 4.61068 10 5.25756 10 6V12C10 13.1046 9.10457 14 8 14H2C0.895431 14 1.61066e-08 13.1046 0 12V6C0 5.25786 0.404598 4.61079 1.00488 4.26562C1.02856 3.75486 1.13035 3.25192 1.30469 2.77832C1.5057 2.23237 1.80046 1.73621 2.17188 1.31836C2.54331 0.900496 2.98442 0.56892 3.46973 0.342773C3.95495 0.116716 4.47482 2.5826e-08 5 0ZM5 6C3.89543 6 3 6.89543 3 8C3 8.63548 3.2977 9.20006 3.75977 9.56641L3.26855 11.75H6.73242L6.24023 9.56641C6.7023 9.20006 7 8.63548 7 8C7 6.89543 6.10457 6 5 6ZM5 1.43555C4.64246 1.43555 4.28835 1.51507 3.95801 1.66895C3.62768 1.82288 3.32707 2.04862 3.07422 2.33301C2.82135 2.61748 2.62027 2.95547 2.4834 3.32715C2.4037 3.54361 2.35027 3.76993 2.31641 4H7.68359C7.64973 3.76993 7.5963 3.54361 7.5166 3.32715C7.37973 2.95547 7.17865 2.61748 6.92578 2.33301C6.67293 2.04862 6.37232 1.82288 6.04199 1.66895C5.71165 1.51507 5.35754 1.43555 5 1.43555Z" fill="#042E6B"/>
  </svg>
);

const NewOption = ({ onSave, onClose }: NewItemProps) => {
  const [title, setTitle] = useState('');

  const clearForm = (): void => setTitle('');

  const closeForm = (): void => {
    clearForm();
    onClose();
  };

  const saveItem = (): void => {
    if (!title || !title.trim().length) {
      return;
    }
    onSave(title);
    closeForm();
  };

  return (
    <div className={cls.item}>
      <div className={cls.colLeft}>
        <button
          className={clsx(cls.btn, cls.btnRound, cls.btnSuccess, !title.trim() && cls.disabled)}
          onClick={saveItem}
          disabled={!title.trim()}
        >
          <CheckOutlined className={clsx(cls.icon, cls.checkIcon)} />
        </button>
      </div>
      <div className={cls.colCenter}>
        <Input
          value={title}
          className={clsx(cls.textField, cls.textFieldNew)}
          bordered={false}
          placeholder="Please, enter title"
          onChange={e => setTitle(e.target.value)} />
      </div>
      <div className={cls.colRight}>
        <button className={clsx(cls.btn, cls.btnRound, cls.btnErrorReverse)} onClick={closeForm}>
          <PlusOutlined className={clsx(cls.icon, cls.deleteBtnIcon)} />
        </button>
      </div>
    </div>
  );
};

export const SettingsOptions = (
  { maxLength = 10, category, readonly = false, title, canMerge = false }: Props,
) => {
  const [beforeUpdate, setBeforeUpdate] = useState('');
  const [items, setItems] = useState<AdminSettingsOption[]>([]);
  const [showNewOption, setNewOption] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [mainId, setMainId] = useState<number | null>(null);

  const hasUnsaved = (): boolean => !(items || []).length
    ? false : items.some((i) => !i.id);

  const hasChanges = (): boolean => JSON.stringify(items.map(i => ({
    category: i.category,
    id: i.id,
    isEnabled: i.isEnabled,
    lockedAt: i.lockedAt,
    name: i.name,
    position: i.position,
    idx: i.idx,
  }))) !== beforeUpdate;

  const countSelected = (): number => {
    return !(items || []).length ? 0 : items.filter((i) => i.isSelected).length;
  };

  const getOptions = async (): Promise<void> => {
    const list = await adminSettingsHttp.getOptionsByCategory(category);
    let result: AdminSettingsOption[] = [];
    if ((list || []).length) {
      result = list.map((i) => ({
        category: i.category,
        id: i.id,
        isEnabled: i.isEnabled,
        lockedAt: i.lockedAt,
        name: i.name,
        position: i.position,
        idx: `${i.id}_${Date.now()}`,
      }));
    }
    setBeforeUpdate(JSON.stringify(result));
    setItems(result);
  };

  const deleteOptions = async (): Promise<void> => {
    let httpResult = false;
    const oldIds: number[] = [];
    const newIds: string[] = [];
    const selected = items.filter((i) => i.isSelected);
    selected.forEach(i => {
      i.id ? oldIds.push(i.id) : newIds.push(i.idx);
    });

    if (oldIds.length) {
      try {
        httpResult = await adminSettingsHttp.deleteOptions(oldIds);
      } catch (e) {
        console.error(e);
      }
    }

    if (httpResult) {
      setItems(
        prev => prev
          .filter(i => !selected.map(s => s.idx).includes(i.idx)),
      );
    } else {
      setItems(prev => prev.filter(i => !newIds.includes(i.idx)));
    }
    !newIds.length && await getOptions();
    notification.success({ message: 'Options are deleted!', placement: 'bottom' });
  };

  const saveOptions = async (options: AdminSettingsOption[]): Promise<void> => {
    try {
      await adminSettingsHttp.setOptions(options);
      await getOptions();
      notification.success({ message: 'Options are updated!', placement: 'bottom' });
    } catch (e) {
      console.error(e);
    }
  };

  const mergeOptions = async (): Promise<void> => {
    if (!mainId || !countSelected()) {
      notification.error({ message: 'Nothing to merge!', placement: 'bottom' });
      return;
    }
    const idsToRemove: number[] = items
      .filter((i) => i.id && i.isSelected && i.id !== mainId)
      .map((i) => i.id) as number[];
    try {
      await adminSettingsHttp.mergeOptions({ mainId, idsToRemove, category });
      await getOptions();
      setIsModalOpen(false);
      notification.success({ message: 'Options are merged!', placement: 'bottom' });
    } catch (e) {
      console.error(e);
      notification.error({ message: 'Merge is not complete!', placement: 'bottom' });
    }
  };

  const mergeChancel = async (): Promise<void> => {
    setIsModalOpen(false);
  };

  const addNewOption = (name: string) => {
    if (items.length >= maxLength) {
      return;
    }

    setItems(prev => ([...prev, {
      category,
      isEnabled: true,
      isSelected: false,
      lockedAt: null,
      name,
      position: prev.length + 1,
      idx: `new_${Date.now()}`,
    }]));
  };

  const editOption = (item: AdminSettingsOption): void => {
    setItems(prev => prev.map(o => o.idx === item.idx ? item : o));
  };

  useEffect(() => {
    getOptions();
  }, []);

  return (
    <div className={cls.wrap}>
      <div className={cls.saveActions}>
        {!readonly && countSelected() ? (
          <ConfirmDelete cb={deleteOptions} title={'Are you sure?'}>
            <button className={clsx(cls.btn, cls.btnRound, cls.btnError)}>
              <PlusOutlined className={clsx(cls.icon, cls.deleteBtnIcon)} />
            </button>
          </ConfirmDelete>
        ) : null}
        {canMerge && !readonly && !hasUnsaved() && countSelected() > 1 ? (
          <button
            className={clsx(cls.btn, cls.btnRound, cls.filedBtn)}
            onClick={() => setIsModalOpen(true)}>
            <MergeIcon />
          </button>
        ) : null}
        {!readonly && items.length && (hasUnsaved() || hasChanges()) ? (
          <button className={clsx(cls.btn, cls.btnRound)} onClick={() => saveOptions(items)}>
            <SaveFilled className={clsx(cls.icon, cls.saveBtnIcon)} />
          </button>
        ) : null}
      </div>

      <div className={cls.table}>
        <div className={cls.tHead}>{title}</div>
        {items.length ? items.map((i: AdminSettingsOption, idx: number) => (
          <div className={clsx(cls.tRow, !i.name.trim() && cls.error)} key={idx}>
            <div className={cls.tCol}>
              {i.isEnabled && !i.lockedAt ? (
                <Checkbox
                  checked={i.isSelected}
                  onChange={e => editOption({ ...i, isSelected: e.target.checked })}
                  className={cls.checkbox}
                  disabled={readonly}
                />
              ) : (
                <div className={cls.lockedBlock}>
                  {i.lockedAt ? <LockIcon className={clsx(cls.icon, cls.lockBtnIcon)} /> : null}
                </div>
              )}
            </div>
            <div className={cls.tCol}>
              <Input
                value={i.name}
                className={cls.textField}
                readOnly={readonly || !i.isEnabled || !!i.lockedAt}
                bordered={false}
                placeholder="Please, enter title"
                onChange={e => editOption({ ...i, name: e.target.value })} />
            </div>
          </div>
        )) : showNewOption ? null : <div className={cls.empty}>Table is empty</div>}
        {showNewOption
          ? <NewOption onSave={addNewOption} onClose={() => setNewOption(false)} />
          : null}
      </div>
      <div className={cls.addActions}>
        <button
          className={clsx(
            cls.btn, cls.btnRound, cls.addBtn,
            (showNewOption || items.length >= maxLength) && cls.disabled,
          )}
          onClick={() => setNewOption(true)}
          disabled={showNewOption || items.length >= maxLength}
        >
          <PlusOutlined className={clsx(cls.icon, cls.addBtnIcon)} />
        </button>
      </div>

      <Modal
        title="Please, select the main option"
        open={isModalOpen}
        onOk={mergeOptions}
        onCancel={mergeChancel}
        footer={[
          <Button className={clsx(cls.modalBtn)} key="back" onClick={mergeChancel}>Chancel</Button>,

          <ConfirmDelete cb={mergeOptions} key="submit" title={'Are you sure?'}>
            <Button
              className={clsx(cls.modalBtn, cls.modalSubmit)}
              type="primary"
            >Merge</Button>
          </ConfirmDelete>,
        ]}
      >
        <Radio.Group
          onChange={e => setMainId(e.target.value)}
          className={cls.radioGroup}
          value={mainId}>
          <Space direction="vertical">
            {items.filter(i => i.isSelected).map(o => (
              <Radio
                key={o.idx}
                className={cls.radio}
                value={o.id}>{o.name}</Radio>
            ))}
          </Space>
        </Radio.Group>
      </Modal>
    </div>
  );
};
